import React, { useState, useEffect } from "react";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  FiCreditCard,
  FiTrash2,
  FiDownload,
  FiPlus,
  FiCheck,
  FiUser,
} from "react-icons/fi";
import { BsCreditCard2Front } from "react-icons/bs";
import { format } from "date-fns";

export default function SubscriptionsBillingSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [professionalPayments, setProfessionalPayments] = useState([]);

  // Fetch all data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, pmRes, bhRes, profRes] = await Promise.all([
        axios
          .get(`${BaseUrl}/api/subscriptions/subscriptions`, {
            withCredentials: true,
          })
          .catch(() => null),
        axios
          .get(`${BaseUrl}/api/subscriptions/payment-methods`, {
            withCredentials: true,
          })
          .catch(() => null),
        axios
          .get(`${BaseUrl}/api/subscriptions/billing/history`, {
            withCredentials: true,
          })
          .catch(() => null),
        // Fetch professional invitations (₹500 payments)
        axios
          .get(`${BaseUrl}/facility/invitations`, { withCredentials: true })
          .catch(() => null),
      ]);

      if (subRes?.data?.data?.[0]) {
        setSubscription(subRes.data.data[0]);
      }
      if (pmRes?.data?.data) {
        setPaymentMethods(pmRes.data.data);
      }
      if (bhRes?.data?.data) {
        setBillingHistory(bhRes.data.data);
      }
      if (profRes?.data?.data) {
        // Filter only professional invitations (these have ₹500 payment)
        const professionals = profRes.data.data.filter(
          (inv) => inv.inviteType === "professional"
        );
        setProfessionalPayments(professionals);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm("Are you sure you want to delete this payment method?"))
      return;

    try {
      await axios.delete(
        `${BaseUrl}/api/subscriptions/payment-methods/${paymentMethodId}`,
        {
          withCredentials: true,
        }
      );
      toast.success("Payment method deleted");
      setPaymentMethods((prev) =>
        prev.filter((pm) => pm._id !== paymentMethodId)
      );
    } catch (error) {
      toast.error("Failed to delete payment method");
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      await axios.put(
        `${BaseUrl}/api/subscriptions/payment-methods/${paymentMethodId}/set-default`,
        {},
        {
          withCredentials: true,
        }
      );
      toast.success("Default payment method updated");
      setPaymentMethods((prev) =>
        prev.map((pm) => ({
          ...pm,
          isDefault: pm._id === paymentMethodId,
        }))
      );
    } catch (error) {
      toast.error("Failed to set default payment method");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy");
    } catch {
      return dateStr || "-";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Combine billing history with professional payments for display
  const getCombinedBillingHistory = () => {
    const combined = [];

    // Add professional invitation payments (₹500 each)
    professionalPayments.forEach((inv) => {
      combined.push({
        _id: inv._id,
        type: "professional",
        createdAt: inv.createdAt,
        description: `Professional Subscription - ${inv.fullName}`,
        amount: 500,
        status: inv.status === "accepted" ? "paid" : "pending",
      });
    });

    // Add regular billing history
    billingHistory.forEach((item) => {
      combined.push({
        ...item,
        type: "subscription",
      });
    });

    // Sort by date descending
    return combined.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  };

  if (loading) {
    return (
      <div className='max-w-3xl animate-pulse'>
        <div className='mb-6 h-8 w-1/3 rounded bg-gray-200'></div>
        <div className='space-y-4'>
          <div className='h-32 rounded bg-gray-200'></div>
          <div className='h-32 rounded bg-gray-200'></div>
          <div className='h-32 rounded bg-gray-200'></div>
        </div>
      </div>
    );
  }

  const combinedHistory = getCombinedBillingHistory();
  const totalSpent =
    professionalPayments.filter((p) => p.status === "accepted").length * 500 +
    billingHistory
      .filter((b) => b.status === "paid")
      .reduce((sum, b) => sum + (b.amount || 0), 0);

  return (
    <div className='max-w-3xl'>
      <h2 className='mb-6 border-b border-gray-200 pb-3 text-2xl font-semibold text-gray-900'>
        Subscriptions & Billing
      </h2>

      <div className='space-y-6'>
        {/* Current Subscription / Professional Summary */}
        <div className='rounded-lg border border-gray-200 p-6'>
          <h3 className='mb-4 text-lg font-semibold text-gray-900'>
            Subscription Overview
          </h3>

          {/* Active Professionals Count */}
          <div className='mb-4 flex items-center justify-between rounded-lg bg-blue-50 p-4'>
            <div className='flex items-center gap-4'>
              <div className='rounded-full bg-blue-100 p-3'>
                <FiUser className='text-xl text-blue-600' />
              </div>
              <div>
                <p className='font-medium text-gray-900'>
                  Active Professionals
                </p>
                <p className='text-sm text-gray-500'>
                  ₹500 per professional added
                </p>
              </div>
            </div>
            <div className='text-right'>
              <p className='text-2xl font-bold text-blue-600'>
                {professionalPayments.length}
              </p>
              <p className='text-xs text-gray-500'>
                Total: {formatCurrency(professionalPayments.length * 500)}
              </p>
            </div>
          </div>

          {/* Add Professional Button */}
          <button
            onClick={() => navigate("/doctors")}
            className='w-full rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 py-3 font-medium text-white transition hover:from-blue-700 hover:to-blue-800'
          >
            + Add New Professional (₹500)
          </button>
        </div>

        {/* Payment Methods */}
        <div className='rounded-lg border border-gray-200 p-6'>
          <div className='mb-4 flex items-center justify-between'>
            <h3 className='text-lg font-semibold text-gray-900'>
              Payment Methods
            </h3>
            <button
              className='flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800'
              onClick={() => {
                toast(
                  "Payment methods are saved automatically during checkout",
                  { icon: "ℹ️" }
                );
              }}
            >
              <FiPlus /> Add New
            </button>
          </div>

          {paymentMethods.length > 0 ? (
            <div className='space-y-3'>
              {paymentMethods.map((pm) => (
                <div
                  key={pm._id}
                  className={`flex items-center justify-between rounded-lg border p-4 ${
                    pm.isDefault
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className='flex items-center gap-4'>
                    <div className='rounded-lg bg-gray-100 p-2'>
                      {pm.type === "card" ? (
                        <BsCreditCard2Front className='text-xl text-gray-600' />
                      ) : (
                        <FiCreditCard className='text-xl text-gray-600' />
                      )}
                    </div>
                    <div>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium'>
                          {pm.type === "card"
                            ? `${pm.cardBrand || "Card"} •••• ${pm.cardLast4}`
                            : pm.upiId || pm.type.toUpperCase()}
                        </span>
                        {pm.isDefault && (
                          <span className='rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700'>
                            Default
                          </span>
                        )}
                      </div>
                      {pm.type === "card" && pm.cardExpiryMonth && (
                        <p className='text-xs text-gray-500'>
                          Expires {pm.cardExpiryMonth}/{pm.cardExpiryYear}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    {!pm.isDefault && (
                      <button
                        onClick={() => handleSetDefaultPaymentMethod(pm._id)}
                        className='p-2 text-gray-400 transition hover:text-blue-600'
                        title='Set as default'
                      >
                        <FiCheck />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeletePaymentMethod(pm._id)}
                      className='p-2 text-gray-400 transition hover:text-red-600'
                      title='Delete'
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='rounded-lg bg-gray-50 py-6 text-center'>
              <FiCreditCard className='mx-auto mb-2 text-4xl text-gray-300' />
              <p className='text-sm text-gray-500'>No saved payment methods</p>
              <p className='mt-1 text-xs text-gray-400'>
                Payment methods are saved when you complete a transaction
              </p>
            </div>
          )}
        </div>

        {/* Billing Summary Stats */}
        <div className='rounded-lg border border-gray-200 p-6'>
          <h3 className='mb-4 text-lg font-semibold text-gray-900'>
            Billing Summary
          </h3>
          <div className='grid grid-cols-3 gap-4'>
            <div className='rounded-lg bg-gray-50 p-4 text-center'>
              <p className='text-2xl font-bold text-gray-900'>
                {formatCurrency(totalSpent)}
              </p>
              <p className='text-xs text-gray-500'>Total Spent</p>
            </div>
            <div className='rounded-lg bg-gray-50 p-4 text-center'>
              <p className='text-2xl font-bold text-gray-900'>
                {combinedHistory.length}
              </p>
              <p className='text-xs text-gray-500'>Transactions</p>
            </div>
            <div className='rounded-lg bg-gray-50 p-4 text-center'>
              <p className='text-2xl font-bold text-blue-600'>
                {professionalPayments.length}
              </p>
              <p className='text-xs text-gray-500'>Professionals</p>
            </div>
          </div>
        </div>

        {/* Billing History */}
        <div className='rounded-lg border border-gray-200 p-6'>
          <h3 className='mb-4 text-lg font-semibold text-gray-900'>
            Billing History
          </h3>
          {combinedHistory.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-200'>
                    <th className='py-3 text-left font-medium text-gray-500'>
                      Date
                    </th>
                    <th className='py-3 text-left font-medium text-gray-500'>
                      Description
                    </th>
                    <th className='py-3 text-left font-medium text-gray-500'>
                      Amount
                    </th>
                    <th className='py-3 text-left font-medium text-gray-500'>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {combinedHistory.map((item) => (
                    <tr key={item._id} className='border-b border-gray-100'>
                      <td className='py-3 text-gray-600'>
                        {formatDate(item.createdAt)}
                      </td>
                      <td className='py-3 text-gray-900'>
                        <div className='flex items-center gap-2'>
                          {item.type === "professional" && (
                            <FiUser className='text-blue-500' />
                          )}
                          {item.description || "Subscription Payment"}
                        </div>
                      </td>
                      <td className='py-3 font-medium text-gray-900'>
                        {formatCurrency(item.amount)}
                      </td>
                      <td className='py-3'>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            item.status === "paid" ||
                            item.status === "success" ||
                            item.status === "accepted"
                              ? "bg-green-100 text-green-700"
                              : item.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status === "accepted" ? "Paid" : item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='rounded-lg bg-gray-50 py-8 text-center'>
              <FiDownload className='mx-auto mb-2 text-4xl text-gray-300' />
              <p className='text-sm text-gray-500'>No billing history yet</p>
              <p className='mt-1 text-xs text-gray-400'>
                Your transactions will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
